<template>
  <img
    v-if="displaySrc"
    :src="displaySrc"
    :alt="alt"
    v-bind="$attrs"
    @error="handleError"
  />
  <slot v-else name="fallback">
    <div class="cached-image-placeholder" v-bind="$attrs"></div>
  </slot>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue'
import { fetchAndCacheImage } from '../composables/useImageCache.js'

const props = defineProps({
  src: {
    type: String,
    default: null,
  },
  alt: {
    type: String,
    default: '',
  },
})

const blobUrl = ref(null)
const blobError = ref(false) // Track if blob URL failed to load
const directError = ref(false) // Track if direct src also failed

// Priority: cached blob URL → direct src → null (show fallback)
const displaySrc = computed(() => {
  // If blob URL exists and hasn't errored, use it (fastest - from cache)
  if (blobUrl.value && !blobError.value) return blobUrl.value
  // Otherwise use direct src if available and not errored
  if (props.src && !directError.value) return props.src
  // Both failed, show fallback slot
  return null
})

// Revoke previous blob URL when component unmounts or src changes
function revokeBlobUrl() {
  if (blobUrl.value && blobUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(blobUrl.value)
  }
}

async function tryCache() {
  if (!props.src) return

  try {
    // Try to fetch and cache (will fail if CORS blocks it)
    const url = await fetchAndCacheImage(props.src)
    if (url) {
      blobUrl.value = url
    }
    // If null (CORS failed), keep showing props.src directly
  } catch {
    // Ignore - direct src is already showing
  }
}

function handleError() {
  // Determine which source failed
  if (blobUrl.value && !blobError.value) {
    // Blob URL failed, fall back to direct src
    blobError.value = true
  } else {
    // Direct src also failed, show fallback slot
    directError.value = true
  }
}

// Watch for src changes
watch(() => props.src, (newSrc) => {
  revokeBlobUrl()
  blobUrl.value = null
  blobError.value = false
  directError.value = false

  if (newSrc) {
    // Try caching in background (non-blocking)
    tryCache()
  }
}, { immediate: true })

// Cleanup on unmount
onBeforeUnmount(() => {
  revokeBlobUrl()
})
</script>

<style scoped>
.cached-image-placeholder {
  background: var(--color-gray-200, #e5e7eb);
}
</style>
